import React, { useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
// Import util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';
import { pickCategoryFields } from '../../../../util/fieldHelpers';
// Import shared components
import { H3, ListingLink } from '../../../../components';
// Import modules from this directory
import ErrorMessage from './ErrorMessage';
import EditListingDetailsForm, { getSchools } from './EditListingDetailsForm';
import css from './EditListingDetailsPanel.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../../../ducks/user.duck';
import { getUserSubscription, getUserType } from '../../../../util/userDataExtraction';
import {
  calculateTotalFollowers,
  getInitialValues,
  getListingTitle,
  getTransactionInfo,
  handleImageUploads,
  hasSetListingType,
  processAffiliationField,
  processArrayField,
  setNoAvailabilityForUnbookableListings,
  updateUserProfile,
} from '../../../../util/editListingHelpers';
import { showErrorToast, showSuccessToast } from '../../../../util/toast';
import { FieldConfigKey, SUBSCRIPTION_TYPES, TalentLevel, UserType } from '../../../../util/enums';
import { formatNumberCompact, isArrayLength } from '../../../../util/genericHelpers';
import moment from 'moment';
import EditListingLayoutSideNav from '../EditListingLayoutSideNav/EditListingLayoutSideNav';
import { updateProfile } from '../../../ProfileSettingsPage/ProfileSettingsPage.duck';

const EditListingDetailsPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    onSubmit,
    onListingTypeChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    config,
    onNextTab,
    viewport,
  } = props;

  const listingImages = listing?.images || [];
  const { SOCIAL_MEDIA, SOCIAL_MEDIA_LINK } = FieldConfigKey;
  const listingImageConfig = config.layout.listingImage;
  const dispatch = useDispatch();
  const currentUser = useSelector(userSelector);
  const subscription = getUserSubscription(currentUser);
  const userType = getUserType(currentUser);
  const { userFields, userTypes } = config.user;
  const classes = classNames(rootClassName || css.root, className);
  const { publicData, state } = listing?.attributes || {};
  const listingTypes = config.listing.listingTypes;
  const listingFields = config.listing.listingFields;
  const listingCategories = config.categoryConfiguration.categories;
  const categoryKey = config.categoryConfiguration.key;
  const filteredSocialMediaOptions =
    isArrayLength(listingFields) &&
    listingFields?.filter(config => [SOCIAL_MEDIA, SOCIAL_MEDIA_LINK].includes(config?.key));
  const { hasExistingListingType, existingListingTypeInfo } = hasSetListingType(publicData);
  const hasValidExistingListingType =
    hasExistingListingType &&
    !!listingTypes.find(conf => {
      const listinTypesMatch = conf.listingType === existingListingTypeInfo.listingType;
      const unitTypesMatch = conf.transactionType?.unitType === existingListingTypeInfo.unitType;
      return listinTypesMatch && unitTypesMatch;
    });
  const initialValues = getInitialValues(
    props,
    existingListingTypeInfo,
    listingTypes,
    listingFields,
    listingCategories,
    categoryKey,
    currentUser,
    userFields
  );

  const noListingTypesSet = listingTypes?.length === 0;
  const hasListingTypesSet = listingTypes?.length > 0;
  const canShowEditListingDetailsForm =
    hasListingTypesSet && (!hasExistingListingType || hasValidExistingListingType);
  const isPublished = listing?.id && state !== LISTING_STATE_DRAFT;
  const [updating, setUpdating] = useState(false);
  const [ready, setReady] = useState(false);
  // Main function to handle form submission
  const handleSubmit = async (values, skipNextTab) => {
    setUpdating(true);
    const cleanupTimeouts = [];
    try {
      const {
        email,
        firstName,
        lastName,
        userType,
        phoneNumber,
        dateOfBirth,
        pictures,
        logo,
        listingType,
        transactionProcessAlias,
        unitType,
        description,
        pub_description,
        location,
        price,
        social_media,
        division = null,
        conference = null,
        school = null,
        profileImageIndex,
        tradingCardImageIndex,
        pub_agency,
        bannerImage,
        profilePicture,
        favoriteProTeams,
        favoriteCollegiateTeams,
        pub_sports,
        pub_interest,
        pub_school_or_group_name,
        agencyName,
        organizationName,
        age,
        totalFollowers,
        subscriptionType,
        pub_agency_custom,
        agency_name,
        highSchoolName,
        ...rest
      } = values;
      const followersCount = calculateTotalFollowers(values?.profileDetails);
      const { selectedPlace: { address = '', origin = null } = {} } = location || {};
      const title = getListingTitle(
        organizationName,
        agencyName,
        pub_school_or_group_name,
        firstName,
        lastName,
        userType
      );
      const formattedDOB = moment(dateOfBirth).format('M/DD/YYYY');
      const formattedAge = moment().diff(dateOfBirth, 'years');
      const defaultSubscriptionMap = {
        [UserType.SCHOOL]: SUBSCRIPTION_TYPES.GROUP_STANDARD,
        [UserType.BRAND]: SUBSCRIPTION_TYPES.BRAND_STANDARD,
      };

      const subscriptionEntry =
        subscription && defaultSubscriptionMap[userType]
          ? {
              subscriptionType: subscription || defaultSubscriptionMap[userType],
            }
          : {};

      const updateValues = {
        geolocation: origin,
        title,
        description: pub_description || description,
        price,
        publicData: {
          ...subscriptionEntry,
          ...(followersCount && {
            totalFollowers: followersCount,
          }),
          ...(dateOfBirth && {
            dateOfBirth: formattedDOB,
            age: formattedAge,
          }),
          listingType,
          transactionProcessAlias,
          unitType,
          location: {
            address: address || null,
          },
          division,
          conference,
          school,
          highSchoolName,
          agencyName,
          socialMedia: social_media,
          ...(isArrayLength(values?.affiliations) && {
            affiliatedOrganizations: processAffiliationField(values?.affiliations),
          }),
          ...(pub_agency && {
            pub_agency: {
              name: pub_agency.value?.trim(),
              id: pub_agency.id,
            },
            agency_name: pub_agency.value?.trim(),
          }),
          bannerImage,
          // Process array fields consistently
          ...(isArrayLength(favoriteProTeams) && {
            favoriteProTeams: processArrayField(favoriteProTeams),
          }),
          ...(isArrayLength(favoriteCollegiateTeams) && {
            favoriteCollegiateTeams: processArrayField(favoriteCollegiateTeams),
          }),
          ...(isArrayLength(pub_sports) && {
            pub_sports: processArrayField(pub_sports),
          }),
          ...(isArrayLength(pub_interest) && {
            pub_interest: processArrayField(pub_interest),
          }),
          ...rest,
        },
        ...setNoAvailabilityForUnbookableListings(transactionProcessAlias),
      };

      if (division && conference) {
        const selectedSchool = getSchools(division, conference).find(s => s.school === school);

        if (selectedSchool) {
          const { city, state, nickname } = selectedSchool || {};

          Object.assign(updateValues.publicData, {
            pub_current_town: city,
            pub_state: state,
            schoolDisplayName: nickname,
          });
        }
      }

      if (currentUser?.id) {
        const { images, uploadedProfilePicture } = await handleImageUploads({
          pictures,
          logo: isArrayLength(logo) ? logo : profilePicture,
          listingImageConfig,
          dispatch,
          profileImageIndex: values?.profileImageIndex || 0,
        });
        if (isArrayLength(images)) {
          updateValues.images = images;
        }
        if (tradingCardImageIndex) {
          updateValues.publicData.tradingCardImage = {
            index: tradingCardImageIndex,
            imageId: updateValues.images
              ? updateValues.images[tradingCardImageIndex]?.imageId?.uuid
              : listingImages[tradingCardImageIndex]?.id?.uuid,
          };
        }
        if (profileImageIndex) {
          updateValues.publicData.profileImage = {
            index: profileImageIndex,
            imageId: updateValues.images
              ? updateValues.images[profileImageIndex]?.imageId?.uuid
              : listingImages[profileImageIndex]?.id?.uuid,
          };
        }
        onSubmit(updateValues);

        updateUserProfile({
          uploadedProfilePicture,
          firstName,
          lastName,
          phoneNumber,
          dateOfBirth,
          dispatch,
          username: values?.username,
          bio: values?.bio,
        });
      }

      if (!skipNextTab) {
        cleanupTimeouts.push(
          setTimeout(() => {
            onNextTab();
          }, 2000)
        );
      }
      setReady(true);
      showSuccessToast('Details updated successfully');
    } catch (error) {
      showErrorToast('Error while submitting details');
      setUpdating(false);
    } finally {
      cleanupTimeouts.push(
        setTimeout(() => {
          setUpdating(false);
        }, 500)
      );

      cleanupTimeouts.push(
        setTimeout(() => {
          setReady(false);
        }, 1500)
      );
    }
    // Cleanup function to clear timeouts on unmount or new submission
    return () => {
      cleanupTimeouts.forEach(timeout => clearTimeout(timeout));
    };
  };
  const handleInitialStep = values => {
    if (!values) return;

    const { firstName, lastName, phoneNumber, ...rest } = values;
    const talentLevel = values?.pub_talent_level;

    const collegiateFields = {
      school: null,
      city: null,
      state: null,
      currentCity: null,
      currentState: null,
      homeTownCity: null,
      hometownState: null,
      division: null,
      conference: null,
    };

    const publicData = {
      ...(talentLevel !== TalentLevel.HIGH_SCHOOL && { highSchoolName: null }),
      ...(talentLevel !== TalentLevel.COLLEGIATE && collegiateFields),
      ...rest,
    };

    onSubmit({
      title: `${firstName} ${lastName}`,
      publicData,
    });

    dispatch(
      updateProfile({
        firstName,
        lastName,
        protectedData: { phoneNumber },
      })
    );
  };

  return (
    <div className={classes}>
      <div className={css.contentLayoutWrapper}>
        <EditListingLayoutSideNav listing={listing} />
        <div className={css.detailContent}>
          <H3 as="h1">
            {isPublished ? (
              <FormattedMessage
                id="EditListingDetailsPanel.title"
                values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
              />
            ) : (
              <FormattedMessage
                id="EditListingDetailsPanel.createListingTitle"
                values={{ lineBreak: <br /> }}
              />
            )}
          </H3>
          {canShowEditListingDetailsForm ? (
            <EditListingDetailsForm
              className={css.form}
              initialValues={initialValues}
              saveActionMsg={submitButtonText}
              onSubmit={handleSubmit}
              selectableListingTypes={listingTypes.map(conf =>
                getTransactionInfo([conf], {}, true)
              )}
              hasExistingListingType={hasExistingListingType}
              selectableCategories={listingCategories}
              pickSelectedCategories={values =>
                pickCategoryFields(values, categoryKey, 1, listingCategories)
              }
              categoryPrefix={categoryKey}
              onListingTypeChange={onListingTypeChange}
              listingFieldsConfig={listingFields}
              marketplaceCurrency={config.currency}
              disabled={disabled}
              ready={ready}
              listing={listing}
              updated={panelUpdated}
              updateInProgress={updating}
              fetchErrors={errors}
              userType={userType}
              userFields={userFields}
              userTypes={userTypes}
              config={config}
              filteredSocialMediaOptions={filteredSocialMediaOptions}
              autoFocus
              viewport={viewport}
              onUpdateInitialStep={handleInitialStep}
            />
          ) : (
            <ErrorMessage
              marketplaceName={config.marketplaceName}
              noListingTypesSet={noListingTypesSet}
              invalidExistingListingType={!hasValidExistingListingType}
            />
          )}
        </div>
      </div>
    </div>
  );
};

EditListingDetailsPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  listing: null,
};

EditListingDetailsPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onListingTypeChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingDetailsPanel;
